/**
 * Product Query Hooks - All Product API Calls with Caching
 *
 * CACHED:
 * - Dashboard Products
 * - Hidden Products
 * - GWP By Stage (Aggregated)
 * - GWP By Env Type (Aggregated)
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Product } from "../db_access/product";
import { CacheDebugger } from "./queryClient";

// -------------------------------------------------------------
// DASHBOARD HOOKS
// -------------------------------------------------------------

/**
 * Fetch all products for my workspace (cached 2 min)
 */
export const useProductsDashboard = () => {
    return useQuery({
        queryKey: ["products", "dashboard"],
        queryFn: async () => {
            const res = await Product.get_from_my_workspace();
            const products = await res.json();
            CacheDebugger.fetch("Dashboard Products", products.length);
            return products;
        },
        staleTime: 0,
    });
};

/**
 * Fetch hidden products (cached 5 min)
 */
export const useHiddenProducts = () => {
    return useQuery({
        queryKey: ["products", "hidden"],
        queryFn: async () => {
            const res = await Product.get_hidden();
            const data = await res.json();
            CacheDebugger.fetch("Hidden Products", data.length);
            return data;
        },
        staleTime: 1000 * 60 * 5,
    });
};

/**
 * Fetch total GWP by stage for products (cached 5 min)
 */
export const useProductsGwpByStage = (productIds = []) => {
    const sortedIdsString = [...productIds].sort().join(",");
    const queryKey = ["products", "gwpByStage", sortedIdsString || "none"];

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (productIds.length === 0) return [];
            const res = await Product.get_total_gwp_by_stage(productIds);
            const data = await res.json();
            CacheDebugger.fetch("Products GWP By Stage", productIds.length);
            return data;
        },
        enabled: productIds.length > 0,
        staleTime: 0,
    });
};

/**
 * Fetch total GWP by env type for products (cached 5 min)
 */
export const useProductsGwpByEnvType = (productIds = []) => {
    const sortedIdsString = [...productIds].sort().join(",");
    const queryKey = ["products", "gwpByEnvType", sortedIdsString || "none"];

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (productIds.length === 0) return [];
            const res = await Product.get_total_gwp_by_env_type(productIds);
            const data = await res.json();
            CacheDebugger.fetch("Products GWP By Env Type", productIds.length);
            return data;
        },
        enabled: productIds.length > 0,
        staleTime: 0,
    });
};

// -------------------------------------------------------------
// CACHE INVALIDATION HELPER
// -------------------------------------------------------------

export const useInvalidateProductCache = () => {
    const queryClient = useQueryClient();

    return {
        // Invalidate specific product
        invalidateProduct: (productId) => {
            return queryClient.invalidateQueries({ queryKey: ["product", productId] });
        },

        // Invalidate dashboard data
        invalidateDashboard: () => {
            return queryClient.invalidateQueries({ queryKey: ["products"] });
        },

        // Invalidate everything
        invalidateAll: () => {
            return Promise.all([
                queryClient.invalidateQueries({ queryKey: ["product"] }),
                queryClient.invalidateQueries({ queryKey: ["products"] })
            ]);
        },
    };
};
